import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'live_updates_dev_tools_enabled'
// The native `storage` event only fires in OTHER tabs, so this custom event
// lets the ProfileMenu toggle and the LiveUpdates scene -- mounted
// independently -- observe each other's writes within the same tab.
const CHANGE_EVENT = 'live-updates-dev-tools-enabled-change'

// localStorage access can throw (private browsing, a storage-disabling
// policy) -- this hook runs unconditionally on every /live_updates render,
// including production, so an uncaught throw here would crash the whole
// page (previously impossible: before this feature, the equivalent
// NODE_ENV check was a build-time literal, dead-code-eliminated from every
// deployed bundle, so no localStorage read ever ran there at all).
const readStoredValue = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Shared on/off state for the Live Updates dev tools (the stack-permutation
 * toolbar + fetch mock), persisted in localStorage so it survives reloads and
 * stays in sync between the ProfileMenu toggle and the scene that reads it.
 * Off by default.
 */
export default function useLiveUpdatesDevToolsEnabled(): [
  boolean,
  (next: boolean) => void
] {
  const [enabled, setEnabledState] = useState(readStoredValue)

  useEffect(() => {
    // A real cross-tab 'storage' event only fires after a successful write
    // elsewhere, so re-reading localStorage here is reliable. The same-tab
    // CHANGE_EVENT is different: setEnabled below dispatches it even when
    // the write itself failed (see its own comment), so re-reading storage
    // here would re-derive the STALE persisted value instead of the value
    // the other instance actually just switched to, silently desyncing two
    // instances on this tab (e.g. the ProfileMenu toggle vs. the scene).
    // Carry the value in the event instead of re-reading it.
    const syncFromStorage = () => setEnabledState(readStoredValue())
    const syncFromChangeEvent = (event: Event) => {
      setEnabledState((event as CustomEvent<boolean>).detail)
    }
    window.addEventListener('storage', syncFromStorage)
    window.addEventListener(CHANGE_EVENT, syncFromChangeEvent)
    return () => {
      window.removeEventListener('storage', syncFromStorage)
      window.removeEventListener(CHANGE_EVENT, syncFromChangeEvent)
    }
  }, [])

  const setEnabled = useCallback((next: boolean) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false')
    } catch {
      // Blocked storage -- fall through and still update this tab's
      // in-memory state below, so the toggle at least works for the current
      // session even though it won't survive a reload or sync to other tabs.
    }
    setEnabledState(next)
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }))
  }, [])

  return [enabled, setEnabled]
}
